import React, { useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

/**
 * Reusable collapsible sidebar component
 * @param {Object} props
 * @param {React.ReactNode} props.children - Sidebar content
 * @param {string} props.className - Additional CSS classes
 * @param {number} props.width - Width when expanded (default: 256px)
 * @param {number} props.collapsedWidth - Width when collapsed (default: 0px)
 * @param {boolean} props.defaultCollapsed - Initial collapsed state
 * @param {string} props.position - 'left' or 'right' (default: 'left')
 */
const CollapsibleSidebar = ({
  children,
  className = '',
  width = 256,
  collapsedWidth = 0,
  defaultCollapsed = false,
  position = 'left'
}) => {
  const [isCollapsed, setIsCollapsed] = useState(defaultCollapsed);

  const toggleSidebar = () => {
    setIsCollapsed(!isCollapsed);
  };

  const sidebarWidth = isCollapsed ? collapsedWidth : width;
  const isLeft = position === 'left';

  return (
    <div 
      className={`relative flex-shrink-0 transition-all duration-300 ease-in-out ${className}`}
      style={{ width: `${sidebarWidth}px` }}
    >
      {/* Sidebar Content */}
      <div 
        className={`h-full overflow-hidden transition-opacity duration-300 ${
          isCollapsed && collapsedWidth === 0 ? 'opacity-0' : 'opacity-100'
        }`}
      >
        {children}
      </div>

      {/* Toggle Button */}
      <button
        onClick={toggleSidebar}
        className={`absolute top-1/2 transform -translate-y-1/2 z-10 w-6 h-8 bg-white dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 rounded-md shadow-lg hover:bg-neutral-50 dark:hover:bg-neutral-700 transition-colors flex items-center justify-center ${
          isLeft 
            ? isCollapsed 
              ? 'left-2' 
              : '-right-3'
            : isCollapsed 
              ? 'right-2' 
              : '-left-3'
        }`}
        title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        style={{ pointerEvents: 'auto' }}
      >
        {isLeft ? (
          isCollapsed ? (
            <ChevronRight size={14} className="text-neutral-600 dark:text-neutral-400" />
          ) : (
            <ChevronLeft size={14} className="text-neutral-600 dark:text-neutral-400" />
          )
        ) : (
          isCollapsed ? (
            <ChevronLeft size={14} className="text-neutral-600 dark:text-neutral-400" />
          ) : (
            <ChevronRight size={14} className="text-neutral-600 dark:text-neutral-400" />
          )
        )}
      </button>
    </div>
  );
};

export default CollapsibleSidebar;
